import {
  Button,
  ButtonGroup,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
} from '@apollo/orbit';
import React from 'react';

import { useForceLightMonacoTheme } from 'src/app/graph/shared/monacoHelpers/monacoHelpers';
import {
  MONACO_READONLY_OPTIONS,
  StudioEditorContainer,
  useStudioEditor,
} from 'src/hooks/useStudioEditor';

import {
  INITIAL_CONFIRMED_PUBLIC_URL,
  useLocalStorageGraphIdentifier,
  usePerGraphIdentifierLocalStorage,
} from './hooks/usePerGraphIdentifierLocalStorage';

export interface PublicUrlConfirmationModalState {
  isOpen: boolean;
  resolvePublicUrlConfirmed: (value: boolean | PromiseLike<boolean>) => void;
  url: string;
  preflightScript: string | undefined;
  isSubscription: boolean;
  canApproveSingleRun: boolean;
}

/**
 * Modal to confirm the endpoint for a public url
 */
export const PublicUrlConfirmationModal = ({
  isOpen,
  resolvePublicUrlConfirmed,
  url,
  preflightScript,
  isSubscription,
  canApproveSingleRun,
}: PublicUrlConfirmationModalState) => {
  const [saveConfirmation, setSaveConfirmation] = React.useState(true);
  const localStorageGraphIdentifier = useLocalStorageGraphIdentifier({
    shouldNamespaceSandboxByEndpoint: false,
    shouldOnlyStoreForRegisteredGraphs: true,
  });
  const [_, setConfirmedPublicUrls] = usePerGraphIdentifierLocalStorage({
    key: 'hasConfirmedPublicUrl',
    graphIdentifier: localStorageGraphIdentifier,
    stableInitialValue: INITIAL_CONFIRMED_PUBLIC_URL,
  });

  useForceLightMonacoTheme();
  const preflightScriptsEditor = useStudioEditor({
    ...MONACO_READONLY_OPTIONS,
    language: 'javascript',
    defaultValue: preflightScript ?? '',
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => resolvePublicUrlConfirmed(false)}
      size="xl"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <ModalTitle>
            Double-check this URL
            {preflightScript ? ' and preflight script' : ''}
          </ModalTitle>
        </ModalHeader>
        <ModalBody>
          <p>
            This graph is set up to send requests at <strong>{url}</strong>.{' '}
            <br />
            {preflightScript && (
              <div>
                And will run this preflight script:
                <div
                  // Exclude scripts from fullstory
                  className="fs-exclude mt-1 h-32 rounded bg-secondary"
                >
                  <StudioEditorContainer
                    editor={preflightScriptsEditor}
                    className="h-full"
                  />
                </div>
              </div>
            )}
          </p>
          <p>Are you sure you want to continue?</p>
        </ModalBody>
        <ModalFooter>
          <label className="flex items-center">
            <input
              className="mr-1 flex size-3 flex-none cursor-pointer items-center rounded border p-0.5"
              type="checkbox"
              checked={saveConfirmation}
              disabled={!canApproveSingleRun}
              onChange={() =>
                setSaveConfirmation(
                  (currentSaveConfirmation) => !currentSaveConfirmation,
                )
              }
            />
            Remember this preference
          </label>
          <ButtonGroup>
            <Button
              variant="secondary"
              onClick={() => resolvePublicUrlConfirmed(false)}
              type="button"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                if (saveConfirmation) {
                  setConfirmedPublicUrls?.(
                    isSubscription
                      ? { subscriptionUrl: url, preflightScript }
                      : { url, preflightScript },
                  );
                }
                resolvePublicUrlConfirmed(true);
              }}
              type="button"
            >
              Continue
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
